import {
  BooleanField,
  ChipField,
  Datagrid,
  DateField,
  DateInput,
  FunctionField,
  List,
  NumberField,
  ReferenceField,
  SelectInput,
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
  useRefresh,
  useNotify,
} from "react-admin";
import ScrollTopPagination from "./shared/ScrollTopPagination";
import {
  Chip,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
} from "@mui/icons-material";
import { useState } from "react";
import config from "../config";
import { getValidToken } from "../services/tokenRefreshService";
import { useAdminTheme } from "../admin-theme";

/**
 * Filter options for webhook dispatch list
 *
 * Note: Default shows only failures (success = false) to avoid overwhelming UI
 */
const WebhookDispatchFilters = [
  <SelectInput
    source="success"
    label="Status"
    choices={[
      { id: "all", name: "All Dispatches" },
      { id: false, name: "Failed Only (Default)" },
      { id: true, name: "Success Only" },
    ]}
    alwaysOn
  />,
  <SelectInput
    source="permanently_failed"
    label="Permanently Failed"
    choices={[
      { id: true, name: "Yes" },
      { id: false, name: "No" },
    ]}
  />,
  <SelectInput
    source="event_type"
    label="Event Type"
    choices={[
      { id: "order_initiated", name: "Order Initiated" },
      { id: "order_created", name: "Order Created" },
      { id: "order_status_changed", name: "Order Status Changed" },
      { id: "customer_signed_up", name: "Customer Signed Up" },
      { id: "customer_deleted", name: "Customer Deleted" },
      { id: "picking_sla", name: "Picking SLA" },
      { id: "packing_sla", name: "Packing SLA" },
      { id: "pickup_sla", name: "Pickup SLA" },
      { id: "payment_error", name: "Payment Error" },
      { id: "uber_direct_error", name: "Uber Direct Error" },
      { id: "inventory_error", name: "Inventory Error" },
      { id: "general_error", name: "General Error" },
    ]}
  />,
  <DateInput source="created_at_gte" label="Created After" />,
  <DateInput source="created_at_lte" label="Created Before" />,
];

/**
 * Custom pagination component
 */
const WebhookDispatchPagination = () => (
  <ScrollTopPagination rowsPerPageOptions={[10, 25, 50, 100]} />
);

/**
 * Custom row styling based on success/failure status
 */
const listRowStyle = (record: any) => {
  if (record?.permanentlyFailed) {
    return { backgroundColor: 'rgba(255, 0, 60, 0.08)' }; // Dark red tint
  }
  if (!record?.success) {
    return { backgroundColor: 'rgba(255, 184, 0, 0.08)' }; // Dark amber tint
  }
  return { backgroundColor: 'rgba(0, 255, 136, 0.06)' }; // Dark green tint
};

/**
 * Status chip component
 */
const StatusChip = () => {
  const record = useRecordContext();
  const { colors } = useAdminTheme();
  if (!record) return null;

  if (record.permanentlyFailed) {
    return (
      <Chip
        icon={<ErrorIcon />}
        label="Permanently Failed"
        size="small"
        sx={{
          bgcolor: `${colors.errorGlow}15`,
          color: colors.errorGlow,
          border: `1px solid ${colors.errorGlow}40`,
          fontWeight: 600,
          '& .MuiChip-icon': { color: colors.errorGlow },
        }}
      />
    );
  }

  if (!record.success && record.nextRetryAt) {
    return (
      <Chip
        icon={<PendingIcon />}
        label="Retry Scheduled"
        size="small"
        sx={{
          bgcolor: `${colors.warning}15`,
          color: colors.warning,
          border: `1px solid ${colors.warning}40`,
          fontWeight: 600,
          '& .MuiChip-icon': { color: colors.warning },
        }}
      />
    );
  }

  if (!record.success) {
    return (
      <Chip
        icon={<ErrorIcon />}
        label="Failed"
        size="small"
        sx={{
          bgcolor: `${colors.warning}15`,
          color: colors.warning,
          border: `1px solid ${colors.warning}40`,
          fontWeight: 600,
          '& .MuiChip-icon': { color: colors.warning },
        }}
      />
    );
  }

  return (
    <Chip
      icon={<SuccessIcon />}
      label="Success"
      size="small"
      sx={{
        bgcolor: `${colors.successGlow}15`,
        color: colors.successGlow,
        border: `1px solid ${colors.successGlow}40`,
        fontWeight: 600,
        '& .MuiChip-icon': { color: colors.successGlow },
      }}
    />
  );
};

/**
 * Manual retry button component
 */
const RetryButton = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const { colors } = useAdminTheme();
  const [loading, setLoading] = useState(false);

  if (!record || record.success) return null;

  const handleRetry = async () => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || config.API_URL.replace(/\/v1$/, '');
      const token = await getValidToken();

      const response = await fetch(
        `${apiUrl}/v1/admin/webhook-dispatches/${record.id}/retry`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      notify("Webhook retry initiated", { type: "success" });
      refresh();
    } catch (error: any) {
      notify(`Retry failed: ${error.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      startIcon={<RefreshIcon />}
      onClick={handleRetry}
      disabled={loading}
      size="small"
      variant="outlined"
      sx={{
        color: colors.warning,
        borderColor: `${colors.warning}60`,
        bgcolor: `${colors.warning}10`,
        fontWeight: 600,
        '&:hover': { bgcolor: `${colors.warning}20`, borderColor: colors.warning, boxShadow: `0 0 15px ${colors.warning}30` },
      }}
    >
      {loading ? "Retrying..." : "Retry Now"}
    </Button>
  );
};

/**
 * Webhook Dispatches List View
 *
 * Displays webhook dispatch attempts with filtering and status indicators
 *
 * **Default Behavior:**
 * - Shows only failed dispatches (success = false)
 * - This prevents overwhelming the UI with thousands of successful dispatches
 * - Use filters to view all dispatches or only permanent failures
 */
export const WebhookDispatchList = (props: any) => {
  const { colors, adminListStyle } = useAdminTheme();

  const datagridSx = {
    '& .MuiTableCell-root': { color: colors.textPrimary, borderColor: colors.border },
    '& .MuiTableHead-root .MuiTableCell-root': {
      color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase',
      fontSize: '0.75rem', letterSpacing: '0.05em', bgcolor: colors.bgCard,
      borderBottom: `1px solid ${colors.border}`,
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': { bgcolor: `${colors.bgElevated} !important` },
    '& .MuiCheckbox-root': { color: colors.textMuted },
    '& .MuiButtonBase-root': { color: colors.info },
  };

  const listSx = {
    ...adminListStyle,
    '& .MuiInputBase-root': { color: colors.textPrimary, bgcolor: colors.bgElevated },
    '& .MuiInputLabel-root': { color: colors.textSecondary },
    '& .MuiSelect-icon': { color: colors.textSecondary },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
    '& .MuiTablePagination-selectIcon': { color: colors.textSecondary },
  };

  return (
    <List
      {...props}
      filters={WebhookDispatchFilters}
      pagination={<WebhookDispatchPagination />}
      sort={{ field: "created_at", order: "DESC" }}
      perPage={25}
      filterDefaultValues={{ success: false }}
      sx={listSx}
    >
      <Datagrid
        rowStyle={listRowStyle}
        bulkActionButtons={false}
        rowClick="show"
        sx={datagridSx}
      >
        <StatusChip />
        <ReferenceField
          source="webhookEndpointId"
          reference="webhook-endpoints"
          label="Endpoint"
          link="show"
        >
          <TextField source="url" />
        </ReferenceField>
        <ChipField source="eventType" label="Event" />
        <NumberField source="attemptNumber" label="Attempt" />
        <NumberField source="httpStatus" label="HTTP Status" />
        <NumberField source="responseTimeMs" label="Response Time (ms)" />
        <DateField source="createdAt" label="Dispatched At" showTime />
        <DateField source="nextRetryAt" label="Next Retry" showTime />
        <RetryButton />
      </Datagrid>
    </List>
  );
};

/**
 * JSON viewer component for payload display
 */
const JsonViewer = ({ data }: { data: any }) => {
  const { colors } = useAdminTheme();
  if (!data) return <Typography sx={{ color: colors.textMuted }}>No data</Typography>;

  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: colors.bgDeepest,
        color: colors.success,
        padding: 2,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        overflow: "auto",
        maxHeight: "500px",
        fontSize: "0.8rem",
        fontFamily: "monospace",
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Box>
  );
};

/**
 * Webhook Dispatch Show/Detail View
 *
 * Displays comprehensive information about a single webhook dispatch attempt
 * including payload, response details, and retry history
 */
export const WebhookDispatchShow = (props: any) => {
  const { colors } = useAdminTheme();

  const showSx = {
    bgcolor: colors.bgDeepest,
    minHeight: '100vh',
    '& .MuiPaper-root': { bgcolor: colors.bgCard, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: 2, boxShadow: 'none' },
    '& .MuiTypography-root': { color: colors.textPrimary },
    '& .RaLabeled-label': { color: colors.textMuted },
    '& .MuiToolbar-root': { bgcolor: colors.bgCard },
    '& .MuiButtonBase-root': { color: colors.info },
  };

  return (
    <Show {...props} sx={showSx}>
      <SimpleShowLayout>
        {/* Status Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            Dispatch Status
          </Typography>
          <StatusChip />
        </Box>

        <Divider sx={{ my: 2, borderColor: colors.border }} />

        {/* Basic Information */}
        <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Basic Information
        </Typography>
        <ReferenceField
          source="webhookEndpointId"
          reference="webhook-endpoints"
          label="Webhook Endpoint"
          link="show"
        >
          <FunctionField
            render={(record: any) => (
              <Box>
                <Typography variant="body2" fontWeight="bold" sx={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                  {record.url}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Type: {record.type} | Active: {record.isActive ? "Yes" : "No"}
                </Typography>
              </Box>
            )}
          />
        </ReferenceField>
        <TextField source="eventType" label="Event Type" />
        <DateField source="createdAt" label="Dispatched At" showTime />

        <Divider sx={{ my: 2, borderColor: colors.border }} />

        {/* Response Details */}
        <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Response Details
        </Typography>
        <BooleanField source="success" label="Successful" />
        <NumberField source="httpStatus" label="HTTP Status Code" />
        <NumberField source="responseTimeMs" label="Response Time (ms)" />
        <TextField source="errorMessage" label="Error Message" />

        <Divider sx={{ my: 2, borderColor: colors.border }} />

        {/* Retry Information */}
        <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Retry Information
        </Typography>
        <NumberField source="attemptNumber" label="Attempt Number" />
        <DateField source="nextRetryAt" label="Next Retry Scheduled" showTime />
        <BooleanField source="permanentlyFailed" label="Permanently Failed" />

        <FunctionField
          label="Retry Status"
          render={(record: any) => {
            if (record.success) {
              return (
                <Alert severity="success" sx={{ bgcolor: `${colors.successGlow}10`, border: `1px solid ${colors.successGlow}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.successGlow } }}>
                  Webhook delivered successfully on attempt #{record.attemptNumber}
                </Alert>
              );
            }

            if (record.permanentlyFailed) {
              return (
                <Alert severity="error" sx={{ bgcolor: `${colors.errorGlow}10`, border: `1px solid ${colors.errorGlow}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.errorGlow } }}>
                  Webhook failed permanently after {record.attemptNumber} attempts.
                  No more retries will be attempted.
                </Alert>
              );
            }

            if (record.nextRetryAt && new Date(record.nextRetryAt) > new Date()) {
              return (
                <Alert severity="warning" sx={{ bgcolor: `${colors.warning}10`, border: `1px solid ${colors.warning}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.warning } }}>
                  Retry scheduled for{" "}
                  {new Date(record.nextRetryAt).toLocaleString()}
                </Alert>
              );
            }

            return (
              <Alert severity="info" sx={{ bgcolor: `${colors.info}10`, border: `1px solid ${colors.info}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.info } }}>
                Retry pending (attempt #{record.attemptNumber})
              </Alert>
            );
          }}
        />

        <Box sx={{ mt: 2 }}>
          <RetryButton />
        </Box>

        <Divider sx={{ my: 2, borderColor: colors.border }} />

        {/* Payload Section */}
        <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Webhook Payload
        </Typography>
        <FunctionField
          label="Payload"
          render={(record: any) => <JsonViewer data={record.payload} />}
        />

        <Divider sx={{ my: 2, borderColor: colors.border }} />

        {/* Timestamps */}
        <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Timestamps
        </Typography>
        <DateField source="createdAt" label="Created At" showTime />
        <DateField source="updatedAt" label="Updated At" showTime />
      </SimpleShowLayout>
    </Show>
  );
};
